/**
 * User Management Component
 * Admin-only page for managing users (approve, role change, deactivate, delete)
 * Also shows pending invitations in the "Чакащи одобрение" tab
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import {
  useUsersAdmin,
  useApproveUser,
  useChangeUserRole,
  useDeactivateUser,
  useReactivateUser,
} from '@/hooks/useUsers';
import { usePendingInvitations, useResendInvitation, useRevokeInvitation } from '@/hooks/useInvitations';
import { useAuthStore } from '@/store/auth.store';
import type { User } from '@/types/api.types';
import InviteUserModal from './InviteUserModal';
import ConfirmDeleteUserModal from './ConfirmDeleteUserModal';
import { toast } from 'sonner';

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'inactive'>('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    displayName: string;
    email: string;
    isActive: boolean;
  } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const currentUser = useAuthStore((s) => s.user);
  const isSuperAdmin = currentUser?.role === 'super_admin';

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openMenuId]);

  // Fetch users with filters
  const { data, isLoading, error } = useUsersAdmin({
    search: searchTerm || undefined,
    status: statusFilter,
    limit: 100,
  });

  // Fetch pending invitations
  const { data: pendingInvitations } = usePendingInvitations();
  const resendMutation = useResendInvitation();
  const revokeMutation = useRevokeInvitation();

  // Mutations
  const approveUserMutation = useApproveUser();
  const changeRoleMutation = useChangeUserRole();
  const deactivateUserMutation = useDeactivateUser();
  const reactivateUserMutation = useReactivateUser();

  const handleApprove = async (userId: string) => {
    setOpenMenuId(null);
    try {
      await approveUserMutation.mutateAsync(userId);
    } catch {
      alert('Грешка при одобряване на потребител');
    }
  };

  const handleChangeRole = async (userId: string, currentRole: string) => {
    setOpenMenuId(null);
    const newRole = currentRole === 'admin' ? 'employee' : 'admin';
    try {
      await changeRoleMutation.mutateAsync({ userId, role: newRole });
    } catch {
      alert('Грешка при промяна на роля');
    }
  };

  const handleDeactivate = async (userId: string) => {
    setOpenMenuId(null);
    try {
      await deactivateUserMutation.mutateAsync(userId);
    } catch {
      alert('Грешка при деактивиране на потребител');
    }
  };

  const handleReactivate = async (userId: string) => {
    setOpenMenuId(null);
    try {
      await reactivateUserMutation.mutateAsync(userId);
    } catch {
      alert('Грешка при реактивиране на потребител');
    }
  };

  const handleResendInvitation = async (invitationId: string, email: string) => {
    setOpenMenuId(null);
    try {
      await resendMutation.mutateAsync(invitationId);
      toast.success(`Поканата за ${email} е изпратена отново`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Грешка при изпращане на покана';
      if (errorMessage.includes('SMTP') || errorMessage.includes('Email send failed')) {
        toast.error('SMTP грешка: Проблем с email сървъра', {
          description: 'Email сървърът е недостъпен или неправилно конфигуриран.',
          duration: 8000,
        });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleRevokeInvitation = async (invitationId: string, email: string) => {
    setOpenMenuId(null);
    if (confirm(`Сигурни ли сте, че искате да отмените поканата за ${email}?`)) {
      try {
        await revokeMutation.mutateAsync(invitationId);
        toast.success(`Поканата за ${email} е отменена`);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Грешка при отменяне на покана';
        toast.error(errorMessage);
      }
    }
  };

  const users = useMemo(() => data?.users || [], [data?.users]);
  const totalUsers = data?.pagination.total || 0;

  // Filter status counts
  const pendingCount = useMemo(() => {
    return users.filter(u => u.pending_approval && u.is_active).length;
  }, [users]);

  const invitationCount = pendingInvitations?.length || 0;
  const totalPendingCount = pendingCount + invitationCount;

  const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date();

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('bg-BG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadgeColor = (role: string) => {
    if (role === 'super_admin') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    if (role === 'admin') return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  };

  const getRoleLabel = (role: string) => {
    if (role === 'super_admin') return 'Супер Админ';
    if (role === 'admin') return 'Администратор';
    return 'Служител';
  };

  // Check if the current user can perform actions on a target user
  const canManageUser = (targetUser: User) => {
    if (targetUser.role === 'super_admin') return false; // Nobody manages super admins from UI
    if (targetUser.role === 'admin') return isSuperAdmin; // Only super_admin can manage admins
    return true; // Admins can manage employees
  };

  const getStatusInfo = (user: User) => {
    if (!user.is_active) {
      return { label: 'Неактивен', color: 'text-red-500 dark:text-red-400' };
    }
    if (user.pending_approval) {
      return { label: 'Чака одобрение', color: 'text-yellow-600 dark:text-yellow-400' };
    }
    return { label: 'Активен', color: 'text-green-600 dark:text-green-400' };
  };

  // Filter invitations by search term
  const filteredInvitations = useMemo(() => {
    if (!pendingInvitations) return [];
    if (!searchTerm) return pendingInvitations;
    const term = searchTerm.toLowerCase();
    return pendingInvitations.filter(inv => inv.email.toLowerCase().includes(term));
  }, [pendingInvitations, searchTerm]);

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-dark-bg-primary rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-white dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border-primary rounded-t-lg px-6 py-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
            Одобрявайте нови потребители, управлявайте роли и състояние на акаунти
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Покани потребител
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border-primary px-6 py-4">
        <div className="flex gap-4 items-center">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Търсене по име или email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-200 dark:hover:bg-dark-bg-primary'
              }`}
            >
              Всички ({totalUsers})
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'pending'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-200 dark:hover:bg-dark-bg-primary'
              }`}
            >
              Чакащи одобрение {totalPendingCount > 0 && `(${totalPendingCount})`}
            </button>
            <button
              onClick={() => setStatusFilter('approved')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'approved'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-200 dark:hover:bg-dark-bg-primary'
              }`}
            >
              Одобрени
            </button>
            <button
              onClick={() => setStatusFilter('inactive')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'inactive'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-200 dark:hover:bg-dark-bg-primary'
              }`}
            >
              Неактивни
            </button>
          </div>
        </div>
      </div>

      {/* User List */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Pending Invitations Section - shown in 'pending' and 'all' filters */}
        {(statusFilter === 'pending' || statusFilter === 'all') && filteredInvitations.length > 0 && (
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow dark:shadow-none dark:border dark:border-dark-border-primary overflow-visible">
            <div className="px-5 py-3 bg-orange-50 dark:bg-orange-900/10 border-b border-orange-200 dark:border-orange-800/30">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium text-orange-800 dark:text-orange-300">
                  Изпратени покани ({filteredInvitations.length})
                </span>
              </div>
            </div>
            <table className="w-full divide-y divide-gray-200 dark:divide-dark-border-primary">
              <thead className="bg-gray-50 dark:bg-dark-bg-tertiary">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Роля
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Състояние
                  </th>
                  <th className="w-12 px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-border-primary">
                {filteredInvitations.map((invitation) => {
                  const expired = isExpired(invitation.expiresAt);
                  return (
                    <tr key={`inv-${invitation.id}`} className="hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 h-9 w-9 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">{invitation.email}</div>
                            <div className="text-xs text-gray-500 dark:text-dark-text-muted truncate">
                              Поканен от {invitation.invitedBy.displayName || invitation.invitedBy.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getRoleBadgeColor(invitation.role)}`}>
                          {getRoleLabel(invitation.role)}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {expired ? (
                          <span className="text-xs font-medium text-red-500 dark:text-red-400">Покана изтекла</span>
                        ) : (
                          <div>
                            <span className="text-xs font-medium text-orange-600 dark:text-orange-400">Очаква отговор</span>
                            <div className="text-[11px] text-gray-400 dark:text-dark-text-muted">до {formatDate(invitation.expiresAt)}</div>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 relative">
                        <button
                          onClick={() => setOpenMenuId(openMenuId === `inv-${invitation.id}` ? null : `inv-${invitation.id}`)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary text-gray-500 dark:text-dark-text-muted transition-colors"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                          </svg>
                        </button>

                        {openMenuId === `inv-${invitation.id}` && (
                          <div
                            ref={menuRef}
                            className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-dark-bg-elevated rounded-lg shadow-lg dark:shadow-none border border-gray-200 dark:border-dark-border-primary z-50 py-1"
                          >
                            <button
                              onClick={() => handleResendInvitation(invitation.id, invitation.email)}
                              disabled={resendMutation.isPending}
                              className="w-full text-left px-4 py-2 text-sm text-blue-700 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                            >
                              {expired ? 'Поднови покана' : 'Изпрати отново'}
                            </button>
                            <div className="border-t border-gray-100 dark:border-dark-border-primary my-1"></div>
                            <button
                              onClick={() => handleRevokeInvitation(invitation.id, invitation.email)}
                              disabled={revokeMutation.isPending}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                            >
                              Отмени покана
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Users */}
        {isLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
            Грешка при зареждане на потребители. Моля опитайте отново.
          </div>
        )}

        {!isLoading && !error && users.length === 0 && filteredInvitations.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-dark-text-primary">Няма намерени потребители</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-muted">
              Опитайте да промените филтрите или търсенето
            </p>
          </div>
        )}

        {!isLoading && !error && users.length > 0 && (
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow dark:shadow-none dark:border dark:border-dark-border-primary overflow-visible">
            <table className="w-full divide-y divide-gray-200 dark:divide-dark-border-primary">
              <thead className="bg-gray-50 dark:bg-dark-bg-tertiary">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Потребител
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Роля
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Състояние
                  </th>
                  <th className="w-12 px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-border-primary">
                {users.map((user) => {
                  const status = getStatusInfo(user);
                  return (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 h-9 w-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                            {(user.display_name || user.email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">{user.display_name || user.email}</div>
                            <div className="text-xs text-gray-500 dark:text-dark-text-muted truncate">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 relative">
                        {canManageUser(user) ? (
                          <button
                            onClick={() => setOpenMenuId(openMenuId === user.id ? null : user.id)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary text-gray-500 dark:text-dark-text-muted transition-colors"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                        ) : (
                          <span className="p-1.5 text-gray-300 dark:text-dark-text-muted/30">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M5 8l5 5 5-5" />
                            </svg>
                          </span>
                        )}

                        {/* Dropdown Menu */}
                        {openMenuId === user.id && canManageUser(user) && (
                          <div
                            ref={menuRef}
                            className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-dark-bg-elevated rounded-lg shadow-lg dark:shadow-none border border-gray-200 dark:border-dark-border-primary z-50 py-1"
                          >
                            {user.pending_approval && user.is_active && (
                              <button
                                onClick={() => handleApprove(user.id)}
                                disabled={approveUserMutation.isPending}
                                className="w-full text-left px-4 py-2 text-sm text-green-700 dark:text-green-400 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                              >
                                Одобри
                              </button>
                            )}

                            {!user.pending_approval && user.is_active && (
                              <button
                                onClick={() => handleChangeRole(user.id, user.role)}
                                disabled={changeRoleMutation.isPending}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                              >
                                {user.role === 'admin' ? 'Направи служител' : 'Направи админ'}
                              </button>
                            )}

                            {user.is_active && (
                              <button
                                onClick={() => handleDeactivate(user.id)}
                                disabled={deactivateUserMutation.isPending}
                                className="w-full text-left px-4 py-2 text-sm text-orange-600 dark:text-orange-400 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                              >
                                Деактивирай
                              </button>
                            )}

                            {!user.is_active && (
                              <button
                                onClick={() => handleReactivate(user.id)}
                                disabled={reactivateUserMutation.isPending}
                                className="w-full text-left px-4 py-2 text-sm text-green-700 dark:text-green-400 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                              >
                                Реактивирай
                              </button>
                            )}

                            <div className="border-t border-gray-100 dark:border-dark-border-primary my-1"></div>

                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                setDeleteTarget({
                                  id: user.id,
                                  displayName: user.display_name || user.email,
                                  email: user.email,
                                  isActive: user.is_active,
                                });
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                            >
                              Изтрий
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite User Modal */}
      {showInviteModal && <InviteUserModal onClose={() => setShowInviteModal(false)} />}

      {/* Delete User Confirmation Modal */}
      {deleteTarget && (
        <ConfirmDeleteUserModal
          user={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;
